import { inject, Injectable } from '@angular/core';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { AbilityServiceSignal } from '@casl/angular';
import { filter, map, Observable, switchMap, of, take, tap } from 'rxjs';
import { NAMESPACE } from '../constants/namespace';
import { AUTH_PERMISSIONS_CONFIG } from '../injection-tokens/auth-permissions-provider';
@Injectable({
  providedIn: 'root',
})
export class RbacPermissionsService {
  private readonly authService = inject(Auth0Service);
  private readonly abilityService = inject(AbilityServiceSignal);
  private readonly authPermissionsConfig = inject(AUTH_PERMISSIONS_CONFIG);

  public getUser$(userId?: string): Observable<User | null | undefined> {
    return this.authService.user$.pipe(
      filter((user) => user !== undefined),
      take(1),
      switchMap((user) => {
        if (!user) {
          return of(null);
        }
        return this.fetchAndUpdateAbilities(userId).pipe(map(() => user));
      })
    );
  }

  public fetchAndUpdateAbilities(userId?: string) {
    return this.authPermissionsConfig.permissions.pipe(
      tap((permissions) => {
        if (permissions.length === 0) {
          console.warn(
            'No permissions found for user. Please ensure that the permissions observable is correctly configured and emitting values.'
          );
        }
      }),
      tap((permissions) => this.updateAbilities(permissions, userId))
    );
  }

  // Showtix userId attributes
  private getAttributeForUserId(subject: string): string | null {
    switch (subject) {
      case 'customer:note':
      case 'order:note':
        return 'agentId';
      case 'work-list':
        return 'assignedToUserId';
      case 'batch-job':
        return 'addUserId';
      default:
        return null;
    }
  }
  public updateAbilities(permissions: string[], userId?: string): void {
    const rules = permissions.flatMap((permission: string) => {
      const splitScope = permission.split(':');
      if (!splitScope.length) {
        return [];
      }

      let self = false;

      if (splitScope[splitScope.length - 1] === 'self') {
        splitScope.pop();
        self = true;
      }

      const action = splitScope.pop();
      const subject = splitScope.join(':');
      if (self) {
        const userIdAttribute = this.getAttributeForUserId(subject);
        if (!userIdAttribute) {
          return [];
        }
        return [
          {
            action,
            subject,
            conditions: {
              [userIdAttribute]: userId,
            },
          },
        ];
      } else {
        return [
          {
            action,
            subject,
          },
        ];
      }
    });
    this.abilityService.update(rules);
  }

  public hasRole(role: string): Observable<boolean> {
    return this.authService.user$.pipe(
      map((user) => {
        if (!user) {
          return false;
        }
        const roles = user[`${NAMESPACE}/roles`];
        return roles.includes(role);
      })
    );
  }
}
